/* eslint-disable react/require-default-props */
import React, { useMemo } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Typography,
} from "@material-ui/core";
import { useIntl } from "../../../../../_metronic/i18n/customUseIntl";
import { toLocalDatetimeString } from "../../../utils/datetime-utils";
import EditorJs from "../../../../modules/notifications/Editor";
import type {
  AppNotification,
  NotificationCategory,
  NotificationFormState,
} from "../../../models/Notification/notificationTypes";
import { useNotificationUpload } from "../../../hooks/notifications/useNotificationUpload";
import { useNotificationSave } from "../../../hooks/notifications/useNotificationSave";
import { notificationFormStyles } from "../../../../ThemeProvider";
import NotificationAttachments from "./NotificationAttachments";
import NotificationFormFields from "./NotificationFormFields";

type EditorProps = {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
};

const Editor = EditorJs as React.ComponentType<EditorProps>;

type NotificationFormProps = {
  category: NotificationCategory;
  notification?: AppNotification;
  onClose: (saved?: AppNotification) => void;
};

function buildValidationSchema(intl: ReturnType<typeof useIntl>) {
  return Yup.object({
    title: Yup.string()
      .required(
        intl.formatMessage({ id: "NOTIFICATIONS.VALIDATION.TITLE_REQUIRED" }),
      )
      .min(3, intl.formatMessage({ id: "NOTIFICATIONS.VALIDATION.TITLE_MIN" })),
    groups: Yup.array().min(
      1,
      intl.formatMessage({ id: "NOTIFICATIONS.VALIDATION.GROUP_REQUIRED" }),
    ),
    publishAt: Yup.date()
      .typeError(
        intl.formatMessage({ id: "NOTIFICATIONS.VALIDATION.INVALID_DATE" }),
      )
      .required(
        intl.formatMessage({
          id: "NOTIFICATIONS.VALIDATION.START_DATE_REQUIRED",
        }),
      ),
    expiresAt: Yup.date()
      .typeError(
        intl.formatMessage({ id: "NOTIFICATIONS.VALIDATION.INVALID_DATE" }),
      )
      .nullable()
      .optional()
      .test(
        "expiry-after-publish",
        intl.formatMessage({
          id: "NOTIFICATIONS.VALIDATION.EXPIRY_AFTER_PUBLISH",
        }),
        function validate(expiresAtValue) {
          const { publishAt } = this.parent;

          if (!expiresAtValue) {
            return true;
          }

          if (!publishAt) {
            return true;
          }

          const expiryTime = new Date(expiresAtValue).getTime();
          const publishTime = new Date(publishAt).getTime();

          return expiryTime > publishTime;
        },
      ),
  });
}

const NotificationForm = ({
  category,
  notification,
  onClose,
}: NotificationFormProps) => {
  const intl = useIntl();
  const classes = notificationFormStyles();

  const analyticsLabel =
    category === "HelpCenter" ? "Help Center Notification" : "Notification";

  const {
    attachments,
    isSaving,
    uploadErrors,
    invalidFileErrors,
    triggerSave,
    onFileChange,
    onChipDelete,
    onToggleRemoveAttachment,
    resetSaving,
  } = useNotificationUpload({
    existingAttachmentKeys: notification?.attachmentKeys,
  });

  const { saveError, persistToDb } = useNotificationSave({
    category,
    notification,
    analyticsLabel,
    onClose,
    resetSaving,
  });

  const validationSchema = useMemo(() => buildValidationSchema(intl), [intl]);

  const formik = useFormik<NotificationFormState>({
    initialValues: {
      id: notification?.id ?? "",
      title: notification?.title ?? "",
      groups: notification?.targetGroups ?? [],
      publishAt: toLocalDatetimeString(notification?.publishAt),
      expiresAt: toLocalDatetimeString(notification?.expiresAt),
      active: notification?.id ? notification.active : true,
      isWelcome: notification?.isWelcome ?? false,
      message: notification?.message ?? "",
    },
    validationSchema,
    onSubmit: (values) => {
      triggerSave((result) => {
        if (result.status === "success") {
          void persistToDb(values, attachments);
        } else {
          resetSaving();
        }
      });
    },
  });

  const titleKey = notification
    ? "NOTIFICATIONS.EDIT_NOTIFICATION"
    : "NOTIFICATIONS.NEW_NOTIFICATION";

  return (
    <Box maxWidth={800}>
      <Card style={{ height: "100%" }}>
        <CardHeader
          title={intl.formatMessage({ id: titleKey })}
          titleTypographyProps={{ color: "secondary", variant: "h4" }}
        />
        <CardContent>
          <form onSubmit={formik.handleSubmit}>
            <NotificationFormFields
              formik={formik}
              isSaving={isSaving}
              isHelpCenter={category === "HelpCenter"}
            />

            <NotificationAttachments
              attachments={attachments}
              isSaving={isSaving}
              uploadErrors={uploadErrors}
              invalidFileErrors={invalidFileErrors}
              onFileChange={onFileChange}
              onChipDelete={onChipDelete}
              onToggleRemoveAttachment={onToggleRemoveAttachment}
            />

            <Editor
              value={formik.values.message}
              onChange={(value: string) => {
                formik.setFieldValue("message", value);
              }}
              disabled={isSaving}
            />

            {saveError && (
              <Typography
                variant="body2"
                color="error"
                className={classes.saveError}
              >
                {intl.formatMessage({ id: saveError })}
              </Typography>
            )}

            <Box className={classes.actionsRow}>
              <Button
                className={classes.saveButton}
                type="submit"
                variant="contained"
                color="primary"
                disableElevation
                disabled={isSaving}
              >
                {intl.formatMessage({ id: "GENERAL.SAVE" })}
              </Button>
              <Button
                variant="contained"
                color="primary"
                disableElevation
                disabled={isSaving}
                onClick={() => onClose()}
              >
                {intl.formatMessage({ id: "GENERAL.CANCEL" })}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default NotificationForm;
